import React, { useCallback } from 'react';
import { observer } from 'mobx-react-lite';
import { useLocation } from 'react-router-dom';

import { useBusinessLogic, useNavigation } from '@trader/hooks';
import { isChallengeAccountType } from '@trader/constants';
import { useI18next } from '@trader/services';
import { useMst } from '@trader/store';

import { GoalReached, RemainingTime } from '../pipsTradesValues';

import * as Styled from './styled';

interface ITradingAccounts {
  onClose?: () => void;
}

export const TradingAccounts: React.FC<ITradingAccounts> = observer(
  ({ onClose }) => {
    const location = useLocation();
    const { translate } = useI18next();
    const store = useMst();
    const { isSecondCaseCondition } = useBusinessLogic('second_case');
    const { navigateTo } = useNavigation();

    const tradingAccounts = isSecondCaseCondition
      ? store.user.tradingAccounts.filter(acc => acc.accountType === 'Demo')
      : isChallengeAccountType
      ? store.user.tradingAccounts.filter(acc => {
          const challenge = store.user.getAccountChallenge(acc.tradingId);
          return challenge?.status !== 'DISABLED';
        })
      : store.user.tradingAccounts;

    const changeTradingAccount = async (
      isActive: boolean,
      platformLogin: string
    ) => {
      !isActive &&
        (await store.user.changeTradingAccountAsync.run({
          platformLogin,
          navigation: location?.state?.disableTradingNavigation
            ? undefined
            : navigateTo,
        }));
      !isActive && onClose && onClose();
    };

    const getChallenge = useCallback(tradingId => {
      return store.user.getAccountChallenge(tradingId);
    }, []);

    return (
      <Styled.Root>
        {tradingAccounts.map(tradingAccount => (
          <Styled.Item
            variant='text'
            disabled={isSecondCaseCondition}
            onClick={() => {
              changeTradingAccount(
                tradingAccount.isInUse,
                tradingAccount.platformLogin
              );
            }}
            $isActive={tradingAccount.isInUse}
            key={tradingAccount.tradingId}
          >
            <Styled.TopText>
              {isChallengeAccountType
                ? `${tradingAccount.currencySymbol}${
                    getChallenge(tradingAccount.tradingId)?.challengeAmount || 0
                  }`
                : tradingAccount.platformLogin}
              {tradingAccount.isInUse && (
                <span>{translate('COMMON.LABELS.ACTIVE')}</span>
              )}
            </Styled.TopText>
            {isChallengeAccountType ? (
              <Styled.PipsBottom justifyContent='space-between'>
                <span>
                  ID: {getChallenge(tradingAccount.tradingId)?.accountId}
                </span>
                <div>
                  <RemainingTime
                    value={
                      getChallenge(tradingAccount.tradingId)?.remainingTime
                    }
                  />
                  <GoalReached
                    value={getChallenge(tradingAccount.tradingId)?.goalReached}
                  />
                </div>
              </Styled.PipsBottom>
            ) : (
              <Styled.BottomText>
                {tradingAccount.product} -{' '}
                {translate(
                  `COMMON.LABELS.${tradingAccount.accountType.toUpperCase()}`
                )}{' '}
                - {tradingAccount.currency}
              </Styled.BottomText>
            )}
          </Styled.Item>
        ))}
      </Styled.Root>
    );
  }
);
